/**
 * THE SHELL — a 56px top bar naming the product, the firm and the person, and
 * beneath it the file layout every screen shares.
 *
 * NO ACTIONS LIVE IN THE TOP BAR. It says where you are and who you are; what
 * you can do belongs on the surface you are looking at, where the one
 * saturated element on the screen is the thing to do next. Sign-out is the
 * single exception, because it is about the bar's own subject.
 *
 * Desk screens (the board, the clients) put the desk index in the spine; a
 * work file puts its own sections there. That is the only difference between
 * them, so the layout is not repeated in either.
 */
import { LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import type { ReactNode } from "react";
import type { Principal } from "@leveri/contracts";
import { Avatar, FileLayout, Label, Spine, SpineGroup, SpineItem, SERIF_OPSZ } from "../file/index.js";
import { call } from "../api.js";

/** Logout answers 204 with no body, and the transport is told to expect exactly that. */
const NoBody = z.undefined();

/** The wordmark. The product's own name, in the display face, and nothing else. */
export function Wordmark() {
  return (
    <span className="font-serif text-[22px] font-semibold tracking-[-0.01em] text-ink" style={SERIF_OPSZ}>
      Leveri
    </span>
  );
}

/**
 * Initials, never a photograph: this product does not upload, store or
 * display one. An unresolved name falls back to the email, never to a
 * placeholder person.
 */
function initialsOf(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]!}${parts[parts.length - 1]![0]!}`.toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

export interface TopBarProps {
  principal: Principal;
  onSignedOut: () => void;
}

export function TopBar({ principal, onSignedOut }: TopBarProps) {
  async function signOut() {
    // The session is ended on the server first; the shell then forgets it.
    // A local forget without the call would leave a live session behind.
    try { await call("POST", "/api/auth/logout", NoBody); } finally { onSignedOut(); }
  }
  return (
    <header className="sticky top-0 z-30 h-14 border-b border-rule bg-canvas">
      <div className="flex h-full items-center gap-6 px-5 md:px-10">
        <Link to="/" aria-label="Accounting work" className="shrink-0">
          <Wordmark />
        </Link>
        <div className="min-w-0 truncate text-sm text-stone">{principal.firmName}</div>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <div className="hidden text-right sm:block">
            <div className="text-sm text-ink">{principal.displayName}</div>
            <div className="text-[13px] text-stone">{principal.email}</div>
          </div>
          <Avatar initials={initialsOf(principal.displayName, principal.email)} self />
          <button
            type="button"
            onClick={signOut}
            aria-label="Sign out"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control text-stone hover:bg-band hover:text-ink"
          >
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </header>
  );
}

export interface DeskProps {
  /** Which desk screen is being read. Weight in the spine, never a background. */
  current: "work" | "clients" | "firm";
  /** `wide` for a screen of records; `file` for a screen of reading. */
  measure?: "file" | "wide" | undefined;
  children: ReactNode;
}

/** The desk: the two places there are to be before a file is open. */
export function Desk({ current, measure, children }: DeskProps) {
  return (
    <FileLayout
      measure={measure}
      spine={
        <Spine>
          <SpineGroup label="Leveri">
            <SpineItem as="a" href="/" current={current === "work"}>Accounting work</SpineItem>
            <SpineItem as="a" href="/clients" current={current === "clients"}>Clients</SpineItem>
            <SpineItem as="a" href="/firm" current={current === "firm"}>Your firm</SpineItem>
          </SpineGroup>
        </Spine>
      }
    >
      {children}
    </FileLayout>
  );
}

/**
 * An address that names nothing. It is a designed state, not a crash — and it
 * offers the one way out rather than leaving the reader to edit the URL.
 */
export function NotHere() {
  const navigate = useNavigate();
  return (
    <Desk current="work">
      <Label>Leveri</Label>
      <h1 className="mt-2 font-serif text-[32px] font-medium leading-[1.15] tracking-[-0.015em] text-ink" style={SERIF_OPSZ}>
        There is nothing at this address
      </h1>
      <p className="mt-3 text-sm text-stone">Nothing is shown rather than something wrong.</p>
      <button type="button" onClick={() => navigate("/")} className="mt-5 text-sm font-medium text-forest hover:underline">
        Go to the work
      </button>
    </Desk>
  );
}
